import React from 'react';

import {MODEL_COLORS, getModelColor, getModelDisplayName} from 'src/lib/evalModels';

export interface ModelMetrics {
  kge?: number;
  crps?: number;
  nrmse?: number;
  mae?: number;
  coverage80?: number;
  percentBias?: number;
  kgePersistence?: number;
  kgeLta?: number;
  crpssPersistence?: number;
  crpssLta?: number;
  nrmsePersistence?: number;
  nrmseLta?: number;
  maePersistence?: number;
  maeLta?: number;
  // Lead time metrics (TODO: not yet computed by backend)
  nrmse1d?: number;
  nrmse2d?: number;
  nrmse3d?: number;
  nrmse5d?: number;
  nrmse10d?: number;
  nrmsePersistence1d?: number;
  nrmsePersistence2d?: number;
  nrmsePersistence3d?: number;
  nrmsePersistence5d?: number;
  nrmsePersistence10d?: number;
}

export interface ModelResult {
  model: string;
  metrics: ModelMetrics;
  isBaseline?: boolean;
}

interface ModelComparisonTableProps {
  results: ModelResult[];
  dateRange?: {start: string; end: string};
  product?: 'short-term' | 'seasonal';
}

function formatValue(v: number | undefined, decimals: number = 2): string {
  if (v === undefined || v === null || isNaN(v)) return 'N/A';
  return v.toFixed(decimals);
}

// KGE skill indicator (difference-based, positive = better)
function KgeSkillIndicator({
  kge,
  baseline,
  label,
}: {
  kge?: number;
  baseline?: number;
  label: string;
}) {
  if (kge == null || baseline == null || isNaN(kge) || isNaN(baseline)) return null;
  const diff = kge - baseline;
  const pct = Math.abs(diff * 100).toFixed(0);
  const isPositive = diff > 0;
  const arrow = isPositive ? '▲' : '▼';
  const colorClass = isPositive ? 'text-green-600' : 'text-red-500';

  return (
    <span
      className={`text-[10px] ${colorClass}`}
      title={`KGE ${isPositive ? 'better' : 'worse'} than ${label}`}
    >
      {arrow}
      {pct}%<span className="text-gray-400 ml-0.5">{label}</span>
    </span>
  );
}

// CRPS skill indicator (CRPSS, positive = better)
function CrpsSkillIndicator({crpss, label}: {crpss?: number; label: string}) {
  if (crpss == null || isNaN(crpss)) return null;
  const pct = Math.abs(crpss * 100).toFixed(0);
  const isPositive = crpss > 0;
  const arrow = isPositive ? '▲' : '▼';
  const colorClass = isPositive ? 'text-green-600' : 'text-red-500';

  return (
    <span
      className={`text-[10px] ${colorClass}`}
      title={`${isPositive ? 'Beats' : 'Worse than'} ${label}`}
    >
      {arrow}
      {pct}%<span className="text-gray-400 ml-0.5">{label}</span>
    </span>
  );
}

// nRMSE skill indicator (ratio-based, lower is better)
function NrmseSkillIndicator({
  nrmse,
  baseline,
  label,
}: {
  nrmse?: number;
  baseline?: number;
  label: string;
}) {
  if (nrmse == null || baseline == null || isNaN(nrmse) || isNaN(baseline) || baseline === 0)
    return null;
  const ratio = nrmse / baseline;
  const pct = Math.abs((1 - ratio) * 100).toFixed(0);
  const isPositive = ratio < 1; // Lower nRMSE is better
  const arrow = isPositive ? '▲' : '▼';
  const colorClass = isPositive ? 'text-green-600' : 'text-red-500';

  return (
    <span
      className={`text-[10px] ${colorClass}`}
      title={`nRMSE ${isPositive ? 'better' : 'worse'} than ${label}`}
    >
      {arrow}
      {pct}%<span className="text-gray-400 ml-0.5">{label}</span>
    </span>
  );
}

function MetricWithSkill({
  _value,
  valueStr,
  children,
}: {
  _value?: number;
  valueStr: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-center gap-2">
      <span className="font-semibold">{valueStr}</span>
      {children && <div className="flex flex-col items-end gap-0.5">{children}</div>}
    </div>
  );
}

// MAE skill indicator (ratio-based, lower is better — same logic as nRMSE)
function MaeSkillIndicator({
  mae,
  baseline,
  label,
}: {
  mae?: number;
  baseline?: number;
  label: string;
}) {
  if (mae == null || baseline == null || isNaN(mae) || isNaN(baseline) || baseline === 0)
    return null;
  const ratio = mae / baseline;
  const pct = Math.abs((1 - ratio) * 100).toFixed(0);
  const isPositive = ratio < 1; // Lower MAE is better
  const arrow = isPositive ? '▲' : '▼';
  const colorClass = isPositive ? 'text-green-600' : 'text-red-500';

  return (
    <span
      className={`text-[10px] ${colorClass}`}
      title={`MAE ${isPositive ? 'better' : 'worse'} than ${label}`}
    >
      {arrow}
      {pct}%<span className="text-gray-400 ml-0.5">{label}</span>
    </span>
  );
}

function getSkillIndicators(key: string, m: ModelMetrics): React.ReactNode {
  if (key === 'kge') {
    return (
      <>
        <KgeSkillIndicator kge={m.kge} baseline={m.kgePersistence} label="PER" />
        <KgeSkillIndicator kge={m.kge} baseline={m.kgeLta} label="LTA" />
      </>
    );
  }
  if (key === 'crps') {
    return (
      <>
        <CrpsSkillIndicator crpss={m.crpssPersistence} label="PER" />
        <CrpsSkillIndicator crpss={m.crpssLta} label="LTA" />
      </>
    );
  }
  if (key === 'nrmse') {
    return (
      <>
        <NrmseSkillIndicator nrmse={m.nrmse} baseline={m.nrmsePersistence} label="PER" />
        <NrmseSkillIndicator nrmse={m.nrmse} baseline={m.nrmseLta} label="LTA" />
      </>
    );
  }
  if (key === 'mae') {
    return (
      <>
        <MaeSkillIndicator mae={m.mae} baseline={m.maePersistence} label="PER" />
        <MaeSkillIndicator mae={m.mae} baseline={m.maeLta} label="LTA" />
      </>
    );
  }
  return null;
}

export default function ModelComparisonTable({
  results,
  dateRange,
  product,
}: ModelComparisonTableProps) {
  // Define metrics for comparison
  const metrics: {
    key: keyof ModelMetrics;
    label: string;
    higherBetter: boolean;
    decimals?: number;
  }[] = [
    {key: 'kge', label: 'KGE', higherBetter: true},
    {key: 'crps', label: 'CRPS', higherBetter: false},
    {key: 'nrmse', label: 'nRMSE', higherBetter: false},
    {key: 'mae', label: 'MAE', higherBetter: false, decimals: 1},
    {key: 'percentBias', label: '% Bias', higherBetter: false, decimals: 1},
    // {key: 'coverage80', label: '80% Cov', higherBetter: true, decimals: 1},
  ];

  // Find best model for each metric
  const bestByMetric: Record<string, string | null> = {};
  metrics.forEach(({key, higherBetter}) => {
    let bestModel: string | null = null;
    let bestValue = higherBetter ? -Infinity : Infinity;
    results.forEach(({model, metrics: m}) => {
      const val = m[key] as number | undefined;
      if (val != null && !isNaN(val)) {
        if (key === 'percentBias') {
          // Closest to 0 is best
          if (bestModel === null || Math.abs(val) < Math.abs(bestValue)) {
            bestValue = val;
            bestModel = model;
          }
        } else if (key === 'coverage80') {
          // Closest to 0.8 is best
          if (bestModel === null || Math.abs(val - 0.8) < Math.abs(bestValue - 0.8)) {
            bestValue = val;
            bestModel = model;
          }
        } else if ((higherBetter && val > bestValue) || (!higherBetter && val < bestValue)) {
          bestValue = val;
          bestModel = model;
        }
      }
    });
    bestByMetric[key] = bestModel;
  });

  // Count wins per model
  const wins: Record<string, number> = {};
  results.forEach(({model}) => (wins[model] = 0));
  Object.values(bestByMetric).forEach((model) => {
    if (model) wins[model]++;
  });
  const rankedModels = Object.entries(wins).sort((a, b) => b[1] - a[1]);

  const dateRangeStr = dateRange ? `(${dateRange.start} – ${dateRange.end})` : '';

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
        Model Comparison
        {dateRangeStr && <span className="text-xs font-normal text-gray-500">{dateRangeStr}</span>}
      </h3>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
                Model
              </th>
              {metrics.map((m) => (
                <th
                  key={m.key}
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200 min-w-[120px]"
                >
                  {m.label}
                </th>
              ))}
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
                Wins
              </th>
            </tr>
          </thead>
          <tbody>
            {results.map(({model, metrics: m, isBaseline}) => (
              <tr key={model} className={isBaseline ? 'bg-gray-50' : ''}>
                <td
                  className={`px-4 py-3 border border-gray-200 font-medium ${isBaseline ? 'text-gray-500 italic' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{backgroundColor: getModelColor(model)}}
                    />
                    {getModelDisplayName(model, product)}
                  </div>
                </td>
                {metrics.map(({key, decimals}) => {
                  const val = m[key] as number | undefined;
                  const isBest = bestByMetric[key] === model;
                  const d = decimals ?? 2;
                  const valStr =
                    key === 'percentBias'
                      ? val != null && !isNaN(val)
                        ? `${val.toFixed(d)}%`
                        : 'N/A'
                      : key === 'coverage80'
                        ? val != null && !isNaN(val)
                          ? `${(val * 100).toFixed(0)}%`
                          : 'N/A'
                        : formatValue(val, d);
                  const skillIndicators = isBaseline ? null : getSkillIndicators(key, m);
                  return (
                    <td
                      key={key}
                      className={`px-4 py-3 text-center border border-gray-200 ${
                        isBest
                          ? 'bg-green-100 text-green-800 font-semibold'
                          : isBaseline
                            ? 'text-gray-500 italic'
                            : ''
                      }`}
                    >
                      <MetricWithSkill _value={val} valueStr={valStr}>
                        {skillIndicators}
                      </MetricWithSkill>
                    </td>
                  );
                })}
                {/* Wins */}
                <td
                  className={`px-4 py-3 text-center border border-gray-200 font-semibold ${isBaseline ? 'text-gray-400' : 'text-blue-600'}`}
                >
                  {wins[model]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Best overall summary */}
      <div className="bg-gray-50 border border-gray-200 px-4 py-3 text-sm">
        <strong>Best overall:</strong>{' '}
        {rankedModels[0] ? (
          <>
            {getModelDisplayName(rankedModels[0][0], product)}
            {rankedModels[0][1] > 0 && (
              <span className="text-gray-500 ml-1">
                ({rankedModels[0][1]}/{metrics.length} metrics)
              </span>
            )}
          </>
        ) : (
          'N/A'
        )}
      </div>
    </div>
  );
}

// Lead Time Table component
interface LeadTimeTableProps {
  results: ModelResult[];
}

export function LeadTimeTable({results}: LeadTimeTableProps) {
  const leadTimes = [
    {key: 'nrmse1d' as keyof ModelMetrics, label: 'Day 1'},
    {key: 'nrmse2d' as keyof ModelMetrics, label: 'Day 2'},
    {key: 'nrmse3d' as keyof ModelMetrics, label: 'Day 3'},
    {key: 'nrmse5d' as keyof ModelMetrics, label: 'Day 5'},
    {key: 'nrmse10d' as keyof ModelMetrics, label: 'Day 10'},
  ];

  const persistenceKeys = [
    'nrmsePersistence1d',
    'nrmsePersistence2d',
    'nrmsePersistence3d',
    'nrmsePersistence5d',
    'nrmsePersistence10d',
  ] as const;

  // Check if we have any lead time data
  const hasLeadTimeData = results.some(({metrics: m}) =>
    leadTimes.some((lt) => {
      const val = m[lt.key];
      return val != null && !isNaN(val as number);
    })
  );

  if (!hasLeadTimeData) {
    return (
      <div className="mt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">nRMSE by Lead Time</h4>
        <div className="bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-500 text-center">
          Lead-time metrics not available yet.
        </div>
      </div>
    );
  }

  // Find best model for each lead time (lowest nRMSE)
  const bestByLeadTime: Record<string, string | null> = {};
  leadTimes.forEach(({key}) => {
    let bestModel: string | null = null;
    let bestValue = Infinity;
    results.forEach(({model, metrics: m}) => {
      const val = m[key] as number | undefined;
      if (val != null && !isNaN(val) && val < bestValue) {
        bestValue = val;
        bestModel = model;
      }
    });
    bestByLeadTime[key] = bestModel;
  });

  // Get persistence values (same across all models)
  const persistence = results[0]?.metrics;

  return (
    <div className="mt-6">
      <h4 className="text-sm font-medium text-gray-700 mb-3">nRMSE by Lead Time</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
                Model
              </th>
              {leadTimes.map((lt) => (
                <th
                  key={lt.key}
                  className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200"
                >
                  {lt.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {results.map(({model, metrics: m}) => (
              <tr key={model}>
                <td className="px-4 py-2 border border-gray-200 font-medium">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{backgroundColor: getModelColor(model)}}
                    />
                    {getModelDisplayName(model)}
                  </div>
                </td>
                {leadTimes.map(({key}) => {
                  const val = m[key] as number | undefined;
                  const isBest = bestByLeadTime[key] === model;
                  return (
                    <td
                      key={key}
                      className={`px-4 py-2 text-center border border-gray-200 ${
                        isBest ? 'bg-green-100 text-green-800 font-semibold' : ''
                      }`}
                    >
                      {formatValue(val, 3)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* Persistence baseline row */}
            {persistence && (
              <tr className="bg-gray-50 border-t-2 border-gray-300">
                <td className="px-4 py-2 border border-gray-200 font-medium text-gray-500 italic">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{backgroundColor: MODEL_COLORS.persistence}}
                    />
                    Persistence
                  </div>
                </td>
                {persistenceKeys.map((key) => {
                  const val = persistence[key] as number | undefined;
                  return (
                    <td
                      key={key}
                      className="px-4 py-2 text-center border border-gray-200 text-gray-500 italic"
                    >
                      {formatValue(val, 3)}
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
